import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Heading,
    Link,
    SimpleGrid,
    Tag,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageCard from '../../components/page-card';
import { translate } from '../../i18n';
import { VENUE_VARIANTS, ITERATION_META, VenueIteration, VenueVariant } from './variants';

const LIVE_VARIANT_IDS = new Set(['v3-calm']);

function VariantCard({ variant }: { variant: VenueVariant }) {
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const subText = useColorModeValue('gray.600', 'whiteAlpha.700');
    const navigate = useNavigate();
    const hasLive = LIVE_VARIANT_IDS.has(variant.id);

    return (
        <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="14px"
            overflow="hidden"
            transition="border-color 0.15s, transform 0.15s"
            _hover={{ borderColor: 'blue.400' }}
        >
            <Flex justify="space-between" align="center" px={4} py={3} borderBottom="1px solid" borderColor={borderColor}>
                <Flex align="center" gap={2}>
                    <Box
                        w="14px" h="14px" borderRadius="full"
                        bg={variant.accent}
                        boxShadow={`0 0 10px ${variant.accent}55`}
                    />
                    <Heading as="h3" size="sm" fontWeight="600">{variant.name}</Heading>
                    <Text fontSize="xs" color={subText} fontFamily="mono" letterSpacing="0.06em" textTransform="uppercase">
                        // {variant.pitch}
                    </Text>
                </Flex>
            </Flex>

            <AspectRatio ratio={16 / 9} bg="black">
                <Box position="relative" overflow="hidden">
                    <Box
                        as="iframe"
                        src={variant.htmlPath}
                        scrolling="no"
                        position="absolute"
                        top="0"
                        left="0"
                        w="1920px"
                        h="1080px"
                        border="0"
                        style={{
                            transformOrigin: 'top left',
                            transform: 'scale(0.32)',
                            pointerEvents: 'none',
                        }}
                        title={`${variant.name} preview`}
                    />
                </Box>
            </AspectRatio>

            <Box px={4} py={3}>
                <Text fontSize="13px" color={subText} lineHeight="1.55" mb={variant.rotates ? 2 : 3}>
                    {variant.optimizesFor}
                </Text>
                {variant.rotates && variant.rotationNote && (
                    <Box
                        mb={3} px={3} py={2}
                        bg="rgba(232, 165, 90, 0.08)"
                        border="1px solid"
                        borderColor="rgba(232, 165, 90, 0.25)"
                        borderRadius="8px"
                        fontSize="12px"
                        lineHeight="1.5"
                    >
                        <Text as="span" fontWeight="600" mr={1.5} fontSize="10px" letterSpacing="0.18em" textTransform="uppercase" color="orange.300">
                            {translate('venue_display.showcase.rotation_demo_label')}
                        </Text>
                        <Text as="span" color={subText}>{variant.rotationNote}</Text>
                    </Box>
                )}
                <Flex gap={2} wrap="wrap">
                    {hasLive && (
                        <Button
                            size="sm"
                            colorScheme="green"
                            variant="solid"
                            onClick={() => navigate('/admin/venue-display/live')}
                        >
                            {translate('venue_display.showcase.launch_live')}
                        </Button>
                    )}
                    <Button
                        as={Link}
                        href={variant.htmlPath}
                        isExternal
                        size="sm"
                        colorScheme="blue"
                        variant={hasLive ? 'outline' : 'solid'}
                        rightIcon={<ExternalLinkIcon />}
                        _hover={{ textDecoration: 'none' }}
                    >
                        {hasLive
                            ? translate('venue_display.showcase.launch_mock')
                            : translate('venue_display.showcase.launch_fullscreen')}
                    </Button>
                    <Tag size="sm" colorScheme="gray" variant="subtle" fontFamily="mono" fontSize="10px">
                        {translate('venue_display.showcase.tag_resolution')}
                    </Tag>
                    {variant.rotates && (
                        <Tag size="sm" colorScheme="orange" variant="subtle" fontFamily="mono" fontSize="10px">
                            {translate('venue_display.showcase.tag_rotation')}
                        </Tag>
                    )}
                    {hasLive && (
                        <Tag size="sm" colorScheme="green" variant="subtle" fontFamily="mono" fontSize="10px">
                            {translate('venue_display.showcase.tag_live')}
                        </Tag>
                    )}
                </Flex>
            </Box>
        </Box>
    );
}

function IterationSection({ iteration, variants }: { iteration: VenueIteration; variants: VenueVariant[] }) {
    const meta = ITERATION_META[iteration];
    const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
    const tag = useColorModeValue('gray.500', 'whiteAlpha.600');
    const countLabel = variants.length === 1
        ? translate('venue_display.showcase.date_count_one', { date: meta.date, count: variants.length })
        : translate('venue_display.showcase.date_count_other', { date: meta.date, count: variants.length });
    return (
        <Box mb={10}>
            <Flex justify="space-between" align="baseline" mb={1} px={4}>
                <Heading as="h2" size="md" fontWeight="600">{meta.label}</Heading>
                <Text fontSize="11px" color={tag} fontFamily="mono" letterSpacing="0.16em" textTransform="uppercase">
                    {countLabel}
                </Text>
            </Flex>
            <Text fontSize="13px" color={sub} mb={5} px={4} maxW="900px">{meta.note}</Text>
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5} px={4}>
                {variants.map((v) => <VariantCard key={v.id} variant={v} />)}
            </SimpleGrid>
        </Box>
    );
}

export default function VenueDisplayShowcasePage() {
    const grouped = useMemo(() => {
        const map = new Map<VenueIteration, VenueVariant[]>();
        VENUE_VARIANTS.forEach((v) => {
            const arr = map.get(v.iteration) ?? [];
            arr.push(v);
            map.set(v.iteration, arr);
        });
        return map;
    }, []);

    const introText = useColorModeValue('gray.600', 'whiteAlpha.700');
    const noteBg = useColorModeValue('blue.50', 'whiteAlpha.100');
    const noteBorder = useColorModeValue('blue.200', 'whiteAlpha.300');

    return (
        <PageCard title={translate('venue_display.showcase.title')}>
            <Box px={4} pb={2}>
                <Text fontSize="14px" color={introText} maxW="900px" lineHeight="1.6" mb={2}>
                    {translate('venue_display.showcase.intro')}
                </Text>
                <Text fontSize="13px" color={introText} maxW="900px" lineHeight="1.6" fontStyle="italic">
                    {translate('venue_display.showcase.intro_warning')}
                </Text>
                <Box
                    mt={4} p={4}
                    bg={noteBg} border="1px solid" borderColor={noteBorder}
                    borderRadius="10px"
                    fontSize="13px" lineHeight="1.6"
                >
                    <Text fontWeight="600" mb={1}>{translate('venue_display.showcase.how_to_use_title')}</Text>
                    <Text mb={2}>{translate('venue_display.showcase.how_to_use_p1')}</Text>
                    <Text>{translate('venue_display.showcase.how_to_use_rotation')}</Text>
                </Box>
            </Box>

            <Box mt={8}>
                {(['v3', 'v2', 'v1'] as VenueIteration[]).map((it) => {
                    const variants = grouped.get(it) ?? [];
                    if (!variants.length) return null;
                    return <IterationSection key={it} iteration={it} variants={variants} />;
                })}
            </Box>
        </PageCard>
    );
}
